import React, { useState, useEffect } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { Spin, Form, Input, Select, Button, message, Modal } from "antd";
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  FileProtectOutlined,
  BellOutlined,
  FileSearchOutlined,
} from "@ant-design/icons";
import { useIntl } from "react-intl";
import IntlMessages from "@iso/components/utility/intlMessages";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import Box from "@iso/components/utility/box";
import { incidentService, propertyService } from "@iso/services";
import {
  INVESTIGATION_TYPES,
  STATUSES,
  findIncidentTypeOption,
  findIncidentOutcomeOption,
} from "@iso/constants/incidentOptions";
import moment from "moment";
import { InvestigationSelectionWrapper } from "./InvestigationSelection.styles";

const { Option } = Select;
const { TextArea } = Input;

const FRAMEWORK_CARDS = [
  {
    value: "notification_only",
    title: "Notification Only",
    description:
      "No formal investigation required. Use when the incident only needs to be recorded and relevant parties notified.",
    iconClass: "framework-icon-amber",
    icon: <BellOutlined />,
    features: [
      "No investigation or assignment needed",
      "Quick resolution for minor events",
      "Suitable for low-risk observations",
    ],
  },
  {
    value: "factual",
    title: "Factual Investigation",
    description:
      "A straightforward factual investigation documenting what happened. Captures the key facts without deep root cause analysis.",
    iconClass: "framework-icon-teal",
    icon: <FileSearchOutlined />,
    features: [
      "Documents the facts of the incident",
      "Suitable for straightforward incidents",
      "Quick turnaround with basic findings",
    ],
  },
  {
    value: "five_why",
    title: "5 Whys Investigation",
    description:
      "A light investigation framework for simple incidents. Quick root cause analysis through iterative questioning.",
    iconClass: "framework-icon-purple",
    icon: <FileProtectOutlined />,
    features: [
      "Simple and quick to complete",
      "Best for low to medium severity incidents",
      "Focuses on finding root causes",
    ],
  },
  {
    value: "icam",
    title: "ICAM Investigation",
    description:
      "Comprehensive incident investigation framework. Detailed analysis of contributing factors and systemic issues.",
    iconClass: "framework-icon-indigo",
    icon: <FileProtectOutlined />,
    features: [
      "Comprehensive and detailed analysis",
      "Best for high severity incidents",
      "Identifies systemic contributing factors",
    ],
  },
];

const InvestigationSelection = () => {
  const { incidentId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const intl = useIntl();
  const isReclassify = new URLSearchParams(location.search).get("mode") === "reclassify";

  const translate = (id, fallback) =>
    intl.messages && intl.messages[id] ? intl.formatMessage({ id }) : fallback;
  const [form] = Form.useForm();
  const [incident, setIncident] = useState(null);
  const [investigation, setInvestigation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [propertyUsers, setPropertyUsers] = useState([]);

  useEffect(() => {
    if (incidentId) {
      fetchIncident();
      fetchInvestigation();
    }
  }, [incidentId]);

  useEffect(() => {
    const propertyId = incident?.property_id;
    if (propertyId) {
      propertyService.getListUsersOfProperty(propertyId, true).then((res) => {
        if (res && res.users) {
          setPropertyUsers(res.users);
        }
      });
    }
  }, [incident?.property_id]);

  useEffect(() => {
    if (incident) {
      form.setFieldsValue({
        classification: incident.classification || undefined,
        investigator_id: incident.investigator_id || undefined,
        location_business_area: incident.location_business_area || undefined,
      });
      if (isReclassify && incident.investigation_type) {
        setSelectedType(incident.investigation_type);
      }
    }
  }, [incident, form, isReclassify]);

  const fetchIncident = async () => {
    try {
      setLoading(true);
      const data = await incidentService.getById(incidentId);
      if (data) {
        setIncident(data);
      } else {
        setIncident(null);
      }
    } catch (err) {
      message.error("Failed to load incident details.");
      setIncident(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchInvestigation = async () => {
    try {
      const data = await incidentService.getInvestigation(incidentId);
      setInvestigation(data);
    } catch (err) {
      console.error("Failed to load investigation:", err);
      setInvestigation(null);
    }
  };

  const handleFrameworkClick = (investigationType) => {
    setSelectedType(investigationType);
  };

  const handleSubmit = async () => {
    if (!selectedType) {
      message.warning("Please select an investigation framework first.");
      return;
    }

    // The same type keeps the investigation; only a new type resets it (backend rule).
    const isSameType = selectedType === incident.investigation_type;

    const doSubmit = async () => {
      try {
        const values = await form.validateFields();
        setSubmitting(true);

        if (isReclassify) {
          await incidentService.reclassify(incident.id, {
            ...values,
            investigation_type: selectedType,
          });
          message.success(
            isSameType
              ? "Classification details updated. The investigation was kept."
              : "Investigation type changed successfully. Previous investigation data has been reset."
          );
        } else {
          await incidentService.classify(incident.id, {
            ...values,
            investigation_type: selectedType,
          });
          message.success("Incident classified successfully.");
        }

        history.push("/incidents");
      } catch (err) {
        if (err.errorFields) return;
        message.error(
          err?.response?.data?.message || "Failed to classify incident."
        );
      } finally {
        setSubmitting(false);
      }
    };

    if (isReclassify) {
      Modal.confirm({
        title: translate(
          "incidentPage.reclassify.confirmTitle",
          "Change Investigation Type"
        ),
        content: isSameType
          ? translate(
              "incidentPage.reclassify.confirmSameType",
              "The investigation type is unchanged, so the existing investigation, corrective actions and lessons learned are kept. Only the classification details will be updated."
            )
          : translate(
              "incidentPage.reclassify.confirmContent",
              "Changing the investigation type will reset any existing investigation data. Are you sure you want to proceed?"
            ),
        okText: translate("incidentPage.reclassify.confirmOk", "Confirm"),
        okButtonProps: { style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" } },
        onOk: doSubmit,
      });
    } else {
      doSubmit();
    }
  };

  const getStatusLabel = (status) => {
    const statusOption = STATUSES.find((s) => s.value === status);
    return statusOption ? statusOption.label : status;
  };

  const getTypeLabel = (type) => {
    const typeOption = findIncidentTypeOption(type);
    return typeOption ? typeOption.label : type;
  };

  const getInvestigationLabel = () => {
    if (!incident) return "Not started";
    if (incident.investigation_type) {
      const invType = INVESTIGATION_TYPES.find(
        (t) => t.value === incident.investigation_type
      );
      return invType ? invType.label : incident.investigation_type;
    }
    return "Not started";
  };

  const showInvestigatorField =
    selectedType && selectedType !== "notification_only";

  // Once approved, the backend refuses a type change; only the current type stays selectable.
  const isTypeLocked =
    isReclassify &&
    ["investigation_approved", "lessons_learned_pending_review", "distributed"].includes(
      incident?.status
    );
  const isCardDisabled = (card) =>
    card.disabled || (isTypeLocked && card.value !== incident.investigation_type);

  if (loading) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationSelectionWrapper>
            <div className="loading-container">
              <Spin size="large" />
            </div>
          </InvestigationSelectionWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  if (!incident) {
    return (
      <LayoutWrapper>
        <Box>
          <InvestigationSelectionWrapper>
            <div className="not-found-container">
              <h2 className="not-found-title">Incident not found</h2>
              <span
                className="not-found-link"
                onClick={() => history.push("/incidents")}
              >
                Return to incidents
              </span>
            </div>
          </InvestigationSelectionWrapper>
        </Box>
      </LayoutWrapper>
    );
  }

  return (
    <LayoutWrapper>
      <Box>
        <InvestigationSelectionWrapper>
          {/* Back Button */}
          <button
            className="back-button"
            onClick={() => history.push("/incidents")}
          >
            <ArrowLeftOutlined />
            Back to Incidents
          </button>

          {/* Incident Details Card */}
          <div className="incident-details-card">
            <div className="incident-header">
              <div>
                <h1 className="incident-report-number">
                  {incident.report_number}
                </h1>
                <p className="incident-description">
                  {incident.description || "No description provided"}
                </p>
              </div>
              {incident.severity && (
                <span
                  className={`severity-badge severity-${(incident.severity || "").toLowerCase()}`}
                >
                  {incident.severity} Severity
                </span>
              )}
            </div>

            {/* Short fields — 3 per row */}
            <div className="incident-meta-grid">
              <div>
                <p className="meta-label">Type</p>
                <p className="meta-value">{getTypeLabel(incident.type)}</p>
              </div>
              <div>
                <p className="meta-label">Activity</p>
                <p className="meta-value">{incident.activity || "-"}</p>
              </div>
              <div>
                <p className="meta-label">Outcome / Consequence</p>
                <p className="meta-value">
                  {incident.consequence
                    ? (findIncidentOutcomeOption(incident.consequence)?.label || incident.consequence)
                    : "-"}
                </p>
              </div>
              <div>
                <p className="meta-label">Location</p>
                <p className="meta-value">
                  {incident.location_business_area
                    || (incident.latitude && incident.longitude
                      ? `${Number(incident.latitude).toFixed(5)}, ${Number(incident.longitude).toFixed(5)}`
                      : "-")}
                </p>
              </div>
              <div>
                <p className="meta-label">Reported By</p>
                <p className="meta-value">
                  {incident.reporter_name || "-"}
                </p>
              </div>
              <div>
                <p className="meta-label">Date & Time</p>
                <p className="meta-value">
                  {incident.date_time
                    ? moment(incident.date_time).format("DD/MM/YYYY HH:mm")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="meta-label">Status</p>
                <p className="meta-value">
                  {incident.status_label || getStatusLabel(incident.status)}
                </p>
              </div>
              <div>
                <p className="meta-label">Current Investigation</p>
                <p className="meta-value">{getInvestigationLabel()}</p>
              </div>
              <div>
                <p className="meta-label">Contractor / Visitor Involved</p>
                <p className="meta-value">
                  {incident.contractor_visitor_involved ? "Yes" : "No"}
                </p>
              </div>
              {incident.personnel_notified && (
                <div>
                  <p className="meta-label">Personnel Notified</p>
                  <p className="meta-value">{incident.personnel_notified}</p>
                </div>
              )}
              {incident.risk_assessment_conducted !== undefined && incident.risk_assessment_conducted !== null && (
                <div>
                  <p className="meta-label">SOP / JSA in Place</p>
                  <p className="meta-value">
                    {incident.risk_assessment_conducted ? "Yes" : "No"}
                  </p>
                </div>
              )}
            </div>

            {/* Long-text detail fields — full width, same visual style */}
            {(incident.immediate_actions || incident.injury_details || incident.damage_details || incident.environment_impact_details || incident.scene_secured || incident.risk_assessment_details || incident.risk_assessment_not_completed_reason || (incident.contractor_visitor_involved && incident.contractor_visitor_details)) && (
              <div className="incident-detail-rows">
                {incident.immediate_actions && (
                  <div className="detail-row">
                    <p className="meta-label">Immediate Actions Taken</p>
                    <p className="meta-value">{incident.immediate_actions}</p>
                  </div>
                )}
                {incident.injury_details && (
                  <div className="detail-row">
                    <p className="meta-label">Injury Details</p>
                    <p className="meta-value">{incident.injury_details}</p>
                  </div>
                )}
                {incident.damage_details && (
                  <div className="detail-row">
                    <p className="meta-label">Damage Details</p>
                    <p className="meta-value">{incident.damage_details}</p>
                  </div>
                )}
                {incident.environment_impact_details && (
                  <div className="detail-row">
                    <p className="meta-label">Environmental Impact</p>
                    <p className="meta-value">{incident.environment_impact_details}</p>
                  </div>
                )}
                {incident.scene_secured && (
                  <div className="detail-row">
                    <p className="meta-label">Scene Secured</p>
                    <p className="meta-value">{incident.scene_secured}</p>
                  </div>
                )}
                {incident.risk_assessment_conducted && incident.risk_assessment_details && (
                  <div className="detail-row">
                    <p className="meta-label">SOP / JSA Details</p>
                    <p className="meta-value">{incident.risk_assessment_details}</p>
                  </div>
                )}
                {!incident.risk_assessment_conducted && incident.risk_assessment_not_completed_reason && (
                  <div className="detail-row">
                    <p className="meta-label">Reason SOP / JSA Not in Place</p>
                    <p className="meta-value">{incident.risk_assessment_not_completed_reason}</p>
                  </div>
                )}
                {incident.contractor_visitor_involved && incident.contractor_visitor_details && (
                  <div className="detail-row">
                    <p className="meta-label">Contractor / Visitor Details</p>
                    <p className="meta-value">{incident.contractor_visitor_details}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Page Title */}
          <h2 className="section-title">
            {isReclassify ? (
              <IntlMessages id="incidentPage.reclassify.title" />
            ) : (
              "Classify & Assign Investigation"
            )}
          </h2>
          <p className="section-subtitle">
            {isReclassify ? (
              <IntlMessages id="incidentPage.reclassify.subtitle" />
            ) : (
              "Choose the appropriate investigation framework based on the incident severity and complexity, then complete the classification details below."
            )}
          </p>

          {/* Framework Selection Cards */}
          <div className="framework-grid">
            {FRAMEWORK_CARDS.map((card) => (
              <div
                key={card.value}
                className={`framework-card${selectedType === card.value ? " framework-card-selected" : ""}${isCardDisabled(card) ? " framework-card-disabled" : ""}`}
                onClick={() => !isCardDisabled(card) && handleFrameworkClick(card.value)}
              >
                {card.disabled && (
                  <span className="coming-soon-badge">Coming Soon</span>
                )}
                <div className="framework-card-content">
                  <div className={`framework-icon ${card.iconClass}`}>
                    {card.icon}
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 className="framework-title">{card.title}</h3>
                    <p className="framework-description">{card.description}</p>
                    <div className="framework-features">
                      {card.features.map((feature, idx) => (
                        <div className="framework-feature" key={idx}>
                          <CheckCircleOutlined className="feature-check" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                    {investigation &&
                      investigation.investigation_type === card.value && (
                        <div className="investigation-in-progress">
                          <WarningOutlined />
                          <span>Investigation in progress</span>
                        </div>
                      )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Classification Form */}
          <div className="classification-form-card">
            <h3 className="form-section-title">Classification Details</h3>
            <Form form={form} layout="vertical">
              <Form.Item
                name="classification"
                label="Classification"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Classification is required",
                  },
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="Enter classification details"
                />
              </Form.Item>

              {showInvestigatorField && (
                <Form.Item name="investigator_id" label="Assign Investigator">
                  <Select
                    placeholder="Select investigator"
                    showSearch
                    optionFilterProp="children"
                    allowClear
                  >
                    {propertyUsers.map((u) => (
                      <Option key={u.id} value={u.id}>
                        {u.name}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              )}

              <Form.Item
                name="location_business_area"
                label="Location / Business Area"
              >
                <Input placeholder="e.g. Horticulture, Processing" />
              </Form.Item>
            </Form>

            <div className="form-actions">
              <Button onClick={() => history.push("/incidents")}>Cancel</Button>
              <Button
                type="primary"
                className="btn-classify"
                loading={submitting}
                onClick={handleSubmit}
              >
                {isReclassify ? "Change Investigation Type" : "Classify & Assign"}
              </Button>
            </div>
          </div>

          {/* Guidelines */}
          <div className="guidelines-card">
            <h3 className="guidelines-title">
              <WarningOutlined className="guidelines-icon" />
              Investigation Framework Guidelines
            </h3>
            <ul className="guidelines-list">
              <li>
                <span className="guidelines-bullet">&bull;</span>
                <span>
                  <strong>Notification Only:</strong> Use when the incident only
                  requires recording and notification, no investigation needed
                </span>
              </li>
              <li>
                <span className="guidelines-bullet">&bull;</span>
                <span>
                  <strong>Factual:</strong> Use for straightforward incidents
                  requiring basic fact documentation
                </span>
              </li>
              <li>
                <span className="guidelines-bullet">&bull;</span>
                <span>
                  <strong>5 Whys:</strong> Use for property damage, equipment
                  failures, near misses, and lower severity incidents
                </span>
              </li>
              <li>
                <span className="guidelines-bullet">&bull;</span>
                <span>
                  <strong>ICAM:</strong> Use for personal injuries,
                  environmental incidents, high-risk near misses, and incidents
                  with multiple contributing factors
                </span>
              </li>
              <li>
                <span className="guidelines-bullet">&bull;</span>
                <span>
                  Consider the potential for learning and systemic improvement
                  when choosing your framework
                </span>
              </li>
            </ul>
          </div>
        </InvestigationSelectionWrapper>
      </Box>
    </LayoutWrapper>
  );
};

export default InvestigationSelection;
